import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { approvalService } from '../services/approval-service';
import { Approval, ApprovalStats } from '../types/approval.types';

/**
 * Estado de aprobaciones
 */
interface ApprovalState {
  approvals: Approval[];
  stats: ApprovalStats | null;
  isLoading: boolean;
  error: string | null;

  fetchApprovals: () => Promise<void>;
  approveQuote: (approvalId: string, comments?: string) => Promise<void>;
  rejectQuote: (approvalId: string, comments: string) => Promise<void>;
  requestAdjustment: (
    approvalId: string,
    suggestedPrice: number,
    comments: string
  ) => Promise<void>;
  fetchStats: () => Promise<void>;
  clearError: () => void;
}

export const useApprovalStore = create<ApprovalState>()(
  devtools(
    (set, get) => ({
      approvals: [],
      stats: null,
      isLoading: false,
      error: null,

      fetchApprovals: async () => {
        try {
          set({ isLoading: true, error: null });
          const approvals = await approvalService.getApprovals();
          set({ approvals, isLoading: false });
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al cargar aprobaciones',
            isLoading: false,
          });
        }
      },

      approveQuote: async (approvalId, comments) => {
        try {
          set({ isLoading: true, error: null });
          await approvalService.approveQuote(approvalId, comments);

          // Refetch approvals
          const approvals = await approvalService.getApprovals();
          set({ approvals, isLoading: false });
          // Y los contadores de arriba, que si no quedan diciendo otra cosa.
          void get().fetchStats();
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error || error.response?.data?.message || 'Error al aprobar',
            isLoading: false,
          });
          throw error;
        }
      },

      rejectQuote: async (approvalId, comments) => {
        try {
          set({ isLoading: true, error: null });
          await approvalService.rejectQuote(approvalId, comments);

          const approvals = await approvalService.getApprovals();
          set({ approvals, isLoading: false });
          // Y los contadores de arriba, que si no quedan diciendo otra cosa.
          void get().fetchStats();
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error || error.response?.data?.message || 'Error al rechazar',
            isLoading: false,
          });
          throw error;
        }
      },

      requestAdjustment: async (approvalId, suggestedPrice, comments) => {
        try {
          set({ isLoading: true, error: null });
          await approvalService.requestAdjustment(approvalId, suggestedPrice, comments);

          const approvals = await approvalService.getApprovals();
          set({ approvals, isLoading: false });
          // Y los contadores de arriba, que si no quedan diciendo otra cosa.
          void get().fetchStats();
        } catch (error: any) {
          set({
            error:
              error.response?.data?.error ||
              error.response?.data?.message ||
              'Error al solicitar ajuste',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchStats: async () => {
        try {
          const stats = await approvalService.getStats();
          set({ stats });
        } catch (error: any) {
          console.error('Error fetching stats:', error);
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'ApprovalStore' }
  )
);

export default useApprovalStore;
